import {
  checkWorldFormat,
  packageExtras,
  WORLD_PACKAGE_LIMITS,
  type Lesson,
  type PackageProblem,
  type ServedWorld,
  type SkyMeasure,
  type WorldPackage,
  orderedFeatures,
  orderedStates,
} from '@vrlearning/lesson-schema';

/**
 * `?pakket=<url of wereld.json>` (the kit's `wereld bekijk`): a package read from a folder on the
 * same origin, its files served by their own URLs (media id = file name), and a small lesson in it --
 * a first look, then one step per state of each feature, so Verder walks through them. Nothing is
 * recorded; nothing comes from the API.
 *
 * Only ever the page's own origin: the address is resolved against it and every URL asked for is the
 * resolved, absolute one (never a path a browser could read as `//other.host/…`). `wereld.json` is
 * checked before a single file is asked for, and no more than WORLD_PACKAGE_LIMITS.files are.
 */
export async function packagePreview(
  address: string,
  fetchJson: (url: string) => Promise<unknown>,
  sizeOf: (url: string) => Promise<number>,
  origin: string = globalThis.location?.origin ?? 'http://localhost',
): Promise<
  | { served: ServedWorld; media: Record<string, string>; lesson: Lesson }
  | { problems: PackageProblem[] }
> {
  const resolved = new URL(address, `${origin}/`);
  if (resolved.origin !== origin) throw new Error('Alleen een pakket van deze server');
  const url = resolved.href;
  const json = await fetchJson(url);
  // Its format first, without its files: nothing is asked for that a refused package names.
  const checked = checkWorldFormat(json);
  const pkg = checked.package;
  if (!pkg) return { problems: checked.problems };
  const base = url.slice(0, url.lastIndexOf('/') + 1);
  // The kit's `bekijk` writes `sky.json` beside `wereld.json` when the package has a sky of its own: its
  // measure (sun, horizon). Without it the sun falls back to straight up and the horizon to grey.
  const sky = await fetchJson(`${base}sky.json`).then(
    (found) =>
      typeof found === 'object' && found !== null && 'sun' in found && 'horizon' in found
        ? (found as SkyMeasure)
        : null,
    () => null,
  );
  const picture = packageExtras(pkg).picture;
  const names = [
    ...pkg.terrain.glb,
    pkg.terrain.heights.file,
    ...('file' in pkg.sky.image ? [pkg.sky.image.file] : []),
    ...('file' in pkg.sound ? [pkg.sound.file] : []),
    // A format-2 package's picture (ADR-059): not drawn here, but the headset's check of the served
    // package (prepareWorld) refuses a named file that is not among its files.
    ...(picture !== null ? [picture] : []),
    ...pkg.blocks.flatMap((b) =>
      b.type === 'surface' && 'file' in b.texture ? [b.texture.file] : [],
    ),
  ];
  const unique = [...new Set(names)];
  if (unique.length > WORLD_PACKAGE_LIMITS.files) {
    return { problems: [{ path: '/terrain', message: 'noemt te veel bestanden' }] };
  }
  const media: Record<string, string> = {};
  const files: Record<string, ServedWorld['files'][string]> = {};
  for (const name of unique) {
    media[name] = `${base}${name}`;
    files[name] = { mediaId: name, bytes: await sizeOf(`${base}${name}`), sha256: '', mime: '' };
  }
  const lesson = previewLesson(pkg);
  return {
    served: { itemId: 'pakket', slug: pkg.slug, package: pkg, files, sky },
    media,
    lesson,
  };
}

/**
 * The small lesson a package is looked at in: a first look, then one step per state of each feature,
 * so Verder walks through them. (The preview does not validate it; the headset judges its features
 * and states by the package's own world, `catalogueOfSource`.)
 */
export function previewLesson(pkg: WorldPackage): Lesson {
  const steps: Lesson['steps'] = [
    { id: 'bekijken', title: 'Bekijken', panel: { title: pkg.name.nl, text: pkg.description } },
    // In the maker's order (lesson-schema order.ts).
    ...orderedFeatures(pkg).flatMap((feature) => {
      const f = pkg.features[feature]!;
      return orderedStates(pkg, feature).map((state) => ({
        id: `${feature}-${state}`,
        title: `${f.name}: ${state}`,
        panel: { title: f.name, text: `${f.name}: ${state}` },
        world: { [feature]: state },
      }));
    }),
  ] as Lesson['steps'];
  // The API's test lesson's frame (services/api/src/library/test-lesson.ts, lessonFrame).
  return {
    schemaVersion: 1,
    id: `pakket-${pkg.slug}`,
    title: `Wereldpakket: ${pkg.name.nl}`.slice(0, 120),
    language: 'nl',
    mode: 'vr',
    audience: { ageMin: 10 },
    learningGoals: ['Het wereldpakket bekijken voordat het naar het platform gaat.'],
    scene: { type: 'world', world: pkg.slug },
    steps,
  } as unknown as Lesson;
}
